// voiceEngine — выбор движка распознавания речи. aidepo-64.
//
// Пользователь выбирает движок в настройках (ProfileScreen). Предпочтение
// персистится в localStorage. Эффективный движок выводится из предпочтения и
// фактической поддержки платформы: нативный Web Speech API доступен не везде
// (нет в установленной iOS-PWA, в Firefox по умолчанию выключен, не работает
// офлайн), поэтому всегда есть фолбэк на серверный whisper.

/** Предпочтение пользователя (то, что хранится и показывается в настройках). */
export type VoiceEnginePref = 'auto' | 'device' | 'server';

/** Фактический движок текущей записи. */
export type VoiceEngineEffective = 'native' | 'whisper';

const STORAGE_KEY = 'voice.engine';
const DEFAULT_PREF: VoiceEnginePref = 'auto';

function isPref(value: string | null): value is VoiceEnginePref {
  return value === 'auto' || value === 'device' || value === 'server';
}

/** Текущее предпочтение из localStorage (дефолт 'auto'). */
export function getVoiceEnginePref(): VoiceEnginePref {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return isPref(raw) ? raw : DEFAULT_PREF;
  } catch {
    // localStorage может быть недоступен (приватный режим) — безопасный дефолт.
    return DEFAULT_PREF;
  }
}

/** Сохранить предпочтение. */
export function setVoiceEnginePref(pref: VoiceEnginePref): void {
  try {
    localStorage.setItem(STORAGE_KEY, pref);
  } catch {
    /* недоступность localStorage не критична — просто не запомним выбор */
  }
}

/**
 * Поддерживает ли платформа нативное распознавание речи в принципе.
 * `SpeechRecognition`/`webkitSpeechRecognition` присутствует И это не
 * установленная iOS-PWA (там API есть, но не работает — Safari standalone).
 */
export function isNativeSttSupported(): boolean {
  if (typeof window === 'undefined') return false;
  const hasApi =
    window.SpeechRecognition !== undefined || window.webkitSpeechRecognition !== undefined;
  if (!hasApi) return false;
  // Safari-only флаг navigator.standalone === true → установленная iOS-PWA,
  // где SpeechRecognition присутствует, но не запускается.
  if (navigator.standalone === true) return false;
  return true;
}

/** Нативный движок реально пригоден прямо сейчас (поддержка + сеть — облачный STT). */
export function isNativeSttUsable(): boolean {
  return isNativeSttSupported() && navigator.onLine;
}

/**
 * Эффективный движок для следующей записи. 'server' всегда whisper; 'auto' и
 * 'device' предпочитают нативный и тихо откатываются на whisper, когда он
 * непригоден (нет поддержки / офлайн). 'device' от 'auto' отличается только
 * тем, что в настройках для него показывается пояснение про фолбэк.
 */
export function resolveEngine(pref: VoiceEnginePref): VoiceEngineEffective {
  if (pref === 'server') return 'whisper';
  return isNativeSttUsable() ? 'native' : 'whisper';
}
